import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  Pagination,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import EditUserDialog from './EditUserDialog';
import RolesTable from './RolesTable';
import UserRolesTable from './UserRolesTable';
import UsersTable from './UsersTable';
import SubscriptionsContent from './SubscriptionsContent';
import { getFetchErrorMessage } from '../services/apiService';

interface UserManagementContentProps {
  selectedView: 'roles' | 'users' | 'userroles' | 'subscriptions';
  searchFilter: {column: string, value: string} | null;
  onColumnsUpdate: (columns: string[]) => void;
  visibleColumns?: string[];
  refreshTrigger?: number;
  onDataUpdate?: (data: any[]) => void;
  roleAddTrigger?: number;
  userRoleAddTrigger?: number;
  onUserRoleAddClick?: () => void;
  userRoleDeletePermission?: string;
  userAddTrigger?: number;
  onUserAddClick?: () => void;
  userEditPermission?: string;
  userDeletePermission?: string;
}

const UserManagementContent: React.FC<UserManagementContentProps> = ({ 
  selectedView, 
  searchFilter, 
  onColumnsUpdate, 
  visibleColumns = [], 
  refreshTrigger,
  onDataUpdate,
  roleAddTrigger,
  userRoleAddTrigger,
  onUserRoleAddClick,
  userRoleDeletePermission,
  userAddTrigger,
  onUserAddClick,
  userEditPermission,
  userDeletePermission
}) => {
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  useEffect(() => {
    if (selectedView) {
      setCurrentPage(1);
      fetchData(selectedView);
    }
  }, [selectedView, refreshTrigger]);

  // Apply search filter when data or searchFilter changes
  useEffect(() => {
    if (!searchFilter || !searchFilter.value) {
      setFilteredData(data);
    } else {
      const filtered = data.filter(item => {
        // If column is empty, search in all columns
        if (!searchFilter.column || searchFilter.column === '') {
          return Object.values(item).some(value => {
            if (value == null) return false;
            return value.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
          });
        }
        
        // Otherwise search in specific column
        const cellValue = item[searchFilter.column];
        if (cellValue == null) return false;
        return cellValue.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
      });
      setFilteredData(filtered);
    }
    setCurrentPage(1);
  }, [data, searchFilter]);

  // Update columns when data changes
  useEffect(() => {
    if (data.length > 0) {
      if (onColumnsUpdate) {
        const columns = Object.keys(data[0]);
        onColumnsUpdate(columns);
      }
      if (onDataUpdate) {
        onDataUpdate(data);
      }
    }
  }, [data, onColumnsUpdate, onDataUpdate]);

  const fetchData = async (view: string) => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      let endpoint = '';
      switch (view) {
        case 'roles':
          endpoint = `${apiUrl}/roles`;
          break;
        case 'users':
          endpoint = `${apiUrl}/users`;
          break;
        case 'userroles':
          endpoint = `${apiUrl}/userroles`;
          break;
        case 'subscriptions':
          endpoint = `${apiUrl}/subscriptions`;
          break;
      }
      
      const response = await fetch(endpoint, { credentials: 'include' });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        throw new Error(await getFetchErrorMessage(response, `Nem sikerült betölteni a ${view} adatokat.`));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hiba történt az adatok betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const getViewTitle = (view: string) => {
    const titles: { [key: string]: string } = {
      roles: 'Szerepek',
      users: 'Felhasználók',
      userroles: 'Felhasználói szerepek',
      subscriptions: 'Előfizetések és költségek',
    };
    return titles[view] || view;
  };

  const handleRecordsPerPageChange = (newRecordsPerPage: number) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const formatCellValue = (key: string, value: any) => {
    if (value === null || value === undefined) return '-';
    
    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Igen' : 'Nem';
    }
    
    // Handle dates
    if (key.toLowerCase().includes('date') || key.toLowerCase().includes('at')) {
      if (value) {
        return new Date(value).toLocaleDateString('hu-HU');
      }
      return '-';
    }
    
    // Handle arrays (like roles)
    if (Array.isArray(value)) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((item, index) => (
            <Chip key={index} label={item} size="small" color="primary" />
          ))}
        </Box>
      );
    }
    
    // Handle status-like fields
    if (key.toLowerCase().includes('active') || key.toLowerCase().includes('confirmed')) {
      return (
        <Chip
          label={value ? 'Aktív' : 'Inaktív'}
          color={value ? 'success' : 'default'}
          size="small"
        />
      );
    }
    
    return value.toString();
  };

  const renderActionButtons = (row: any) => {
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Szerkesztés">
          <IconButton 
            size="small" 
            sx={{ color: '#ff8c42' }}
            onClick={() => handleEditClick(row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Törlés">
          <IconButton size="small" sx={{ color: '#f44336' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const handleEditClick = (user: any) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
  };

  const handleEditSuccess = () => {
    // Trigger a refresh of the data
    if (selectedView) {
      fetchData(selectedView);
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  if (!selectedView) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          Válasszon egy menüpontot a bal oldali sávból
        </Typography>
      </Box>
    );
  }

  // Use dedicated RolesTable component for roles view
  if (selectedView === 'roles') {
    return (
      <Box sx={{ 
        flexGrow: 1,
        minWidth: 0,
      }}>
        <RolesTable 
          externalAddTrigger={roleAddTrigger}
          hideAddButton={true}
        />
      </Box>
    );
  }

  // Use dedicated UserRolesTable component for userroles view
  if (selectedView === 'userroles') {
    return (
      <Box sx={{ 
        flexGrow: 1,
        minWidth: 0,
      }}>
        <UserRolesTable 
          externalAddTrigger={userRoleAddTrigger}
          hideAddButton={true}
          onAddClick={onUserRoleAddClick}
          deletePermission={userRoleDeletePermission}
          refreshTrigger={refreshTrigger}
        />
      </Box>
    );
  }

  // Use dedicated UsersTable component for users view
  if (selectedView === 'users') {
    return (
      <Box sx={{ 
        flexGrow: 1,
        minWidth: 0,
      }}>
        <UsersTable 
          externalAddTrigger={userAddTrigger}
          hideAddButton={true}
          onAddClick={onUserAddClick}
          editPermission={userEditPermission}
          deletePermission={userDeletePermission}
          refreshTrigger={refreshTrigger}
        />
      </Box>
    );
  }

  // Use dedicated SubscriptionsContent component for subscriptions view
  if (selectedView === 'subscriptions') {
    return (
      <Box sx={{ 
        flexGrow: 1,
        minWidth: 0,
      }}>
        <SubscriptionsContent 
          refreshTrigger={refreshTrigger}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      flexGrow: 1,
      minWidth: 0, // Prevents flex item from growing beyond container
    }}>
      <Card sx={{ backgroundColor: 'white' }}>
        <CardContent>
          {/* Header with title and records info */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <Typography variant="h5" component="h2" fontWeight="bold">
              {getViewTitle(selectedView)}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Rekordok száma oldalanként:
              </Typography>
              <ButtonGroup size="small" variant="outlined">
                {[25, 50, 100].map((count) => (
                  <Button
                    key={count}
                    variant={recordsPerPage === count ? 'contained' : 'outlined'}
                    onClick={() => handleRecordsPerPageChange(count)}
                    sx={{
                      minWidth: '50px',
                      backgroundColor: recordsPerPage === count ? '#ff8c42' : 'transparent',
                      borderColor: '#ff8c42',
                      color: recordsPerPage === count ? 'white' : '#ff8c42',
                      '&:hover': {
                        backgroundColor: recordsPerPage === count ? '#e67a35' : 'rgba(255, 140, 66, 0.1)',
                      }
                    }}
                  >
                    {count}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#ff8c42' }} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && currentData.length > 0 && (
            <>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  mt: 2, 
                  overflow: 'auto', 
                  maxWidth: '100%',
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      {visibleColumns.map((column) => (
                        <TableCell key={column} sx={{ fontWeight: 'bold' }}>
                          {column}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        Műveletek
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentData.map((row, index) => (
                      <TableRow key={row.id || index} hover>
                        {visibleColumns.map((column) => (
                          <TableCell key={column}>
                            {formatCellValue(column, row[column])}
                          </TableCell>
                        ))}
                        <TableCell sx={{ textAlign: 'center' }}>
                          {renderActionButtons(row)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination and info */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {`${startIndex + 1}-${Math.min(endIndex, filteredData.length)} / ${filteredData.length} rekord`}
                </Typography>
                
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      '&.Mui-selected': {
                        backgroundColor: '#ff8c42',
                        '&:hover': {
                          backgroundColor: '#e67a35',
                        }
                      }
                    }
                  }}
                />
              </Box>
            </>
          )}

          {!loading && !error && currentData.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                Nincsenek adatok
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserManagementContent;
